import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, Phone, ArrowRight, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import DogwoodLogo from '../ui/DogwoodLogo';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { formatApiErrorDetail } from '../../lib/api';

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const inviteName = searchParams.get('name') || '';
  const [formData, setFormData] = useState({
    firstName: inviteName.split(' ')[0] || '',
    lastName: inviteName.split(' ').slice(1).join(' ') || '',
    email: searchParams.get('email') || '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [membershipLevel, setMembershipLevel] = useState(
    searchParams.get('level') === 'junior' ? 'Junior Member' : 'Member'
  );
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    setIsLoading(true);
    try {
      await register({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        membership_level: membershipLevel,
      });
      toast.success('Account created successfully! Welcome to the Women\'s Club.');
      navigate('/welcome');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-32 flex items-center justify-center bg-[#f1e3eb]">
      <div className="w-full max-w-lg px-4 py-12">
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#bd8cae] to-[#bd8cae] flex items-center justify-center mx-auto mb-4">
              <DogwoodLogo size={42} className="text-white" />
            </div>
            <CardTitle className="font-serif text-2xl text-[#3c4a2a]">
              Create Your Account
            </CardTitle>
            <CardDescription className="text-[#3c4a2a]/60">
              Join our community of women leaders
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-[#3c4a2a]">I'm joining as *</Label>
                <div className="grid grid-cols-2 gap-3 mt-2" data-testid="membership-type-selector">
                  {[
                    { value: 'Member', label: 'Member', hint: 'Adult membership' },
                    { value: 'Junior Member', label: 'Junior Member', hint: 'Young women & students' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setMembershipLevel(opt.value)}
                      data-testid={`membership-type-${opt.value === 'Member' ? 'member' : 'junior'}`}
                      className={`rounded-lg border-2 px-4 py-3 text-left transition-colors ${
                        membershipLevel === opt.value
                          ? 'border-[#5d6b3c] bg-[#5d6b3c]/5'
                          : 'border-[#bd8cae]/40 hover:border-[#bd8cae]'
                      }`}
                    >
                      <span className="flex items-center gap-2 font-medium text-[#3c4a2a] text-sm">
                        {membershipLevel === opt.value && <Check className="w-4 h-4 text-[#5d6b3c]" />}
                        {opt.label}
                      </span>
                      <span className="block text-xs text-[#3c4a2a]/60 mt-0.5">{opt.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-[#3c4a2a]">
                    First Name *
                  </Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#3c4a2a]/40" />
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="pl-10 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                      placeholder="First name"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-[#3c4a2a]">
                    Last Name *
                  </Label>
                  <div className="relative mt-2">
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                      placeholder="Last name"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-[#3c4a2a]">
                  Email Address *
                </Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#3c4a2a]/40" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="pl-10 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-[#3c4a2a]">
                  Phone Number
                </Label>
                <div className="relative mt-2">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#3c4a2a]/40" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="pl-10 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                    placeholder="(xxx) xxx-xxxx"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-[#3c4a2a]">
                  Password *
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#3c4a2a]/40" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="pl-10 pr-10 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                    placeholder="Minimum 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3c4a2a]/40 hover:text-[#3c4a2a]"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-[#3c4a2a]">
                  Confirm Password *
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#3c4a2a]/40" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className="pl-10 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                    placeholder="Confirm your password"
                  />
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="terms"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  className="mt-1"
                />
                <Label htmlFor="terms" className="text-sm text-[#3c4a2a]/70 leading-relaxed cursor-pointer">
                  I agree to the{' '}
                  <Link to="/terms" className="text-[#5d6b3c] hover:underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-[#5d6b3c] hover:underline">
                    Privacy Policy
                  </Link>
                </Label>
              </div>

              <Button
                type="submit"
                disabled={isLoading || !agreedToTerms}
                className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white py-6"
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-[#3c4a2a]/60 text-sm">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-[#5d6b3c] hover:text-[#3c4a2a] font-medium"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default RegisterPage;
